/*
 * ------------------------------------------------------------
 * Blog commerce scripts
 * Loaded by the "Blog: article content" section only when the table of contents is shown.
 *
 * Table of contents:
 * - The box is closed on mobile and opened on desktop (1000px and wider).
 * - Desktop side rail: appears once the box has scrolled out of view, highlights the chapter being read
 *   and shows reading progress. It hides before the section after the article reaches it.
 * - The rail sits below the site header, measured on the page, so it follows a header that hides or shows on scroll.
 * - The rail only appears when there is enough room beside the text column; otherwise the box alone is used.
 * - Works in left-to-right and right-to-left languages.
 * ------------------------------------------------------------
 */

(() => {
  const box = document.querySelector('[data-blog-toc]');

  if (!box) return;

  if (window.matchMedia('(min-width: 1000px)').matches) {
    box.open = true;
  }

  const RAIL_WIDTH = 220;
  const RAIL_GAP = 48;
  const SCREEN_EDGE = 24;
  const SPACE_BELOW_HEADER = 40;
  const SPACE_BEFORE_NEXT_SECTION = 32;

  const header = document.querySelector('.shopify-section--header, #shopify-section-header, header');

  // Height of the header area currently covering the top of the screen
  const getHeaderOffset = () => {
    if (!header) return 0;
    const bottom = header.getBoundingClientRect().bottom;
    return bottom > 0 ? bottom : 0;
  };

  const setHeaderOffsetVariable = (headerOffset) => {
    document.documentElement.style.setProperty('--blog-header-offset', `${Math.round(headerOffset)}px`);
  };

  setHeaderOffsetVariable(getHeaderOffset());

  const rail = document.querySelector('[data-blog-toc-rail]');
  const contentSections = document.querySelectorAll('[data-blog-content]');
  const textColumn = box.parentElement.querySelector(':scope > .prose');

  if (!rail || !textColumn || contentSections.length === 0) return;

  // Moved to the end of the page so no parent element can affect its fixed position
  document.body.appendChild(rail);
  rail.hidden = false;

  const firstContent = contentSections[0];
  const lastContent = contentSections[contentSections.length - 1];
  const progressBar = rail.querySelector('[data-blog-toc-progress]');
  const track = progressBar ? progressBar.parentElement : null;
  const isRtl = getComputedStyle(document.documentElement).direction === 'rtl';

  const chapters = Array.from(rail.querySelectorAll('a[href^="#chapter-"]'))
    .map((link) => ({ link, heading: document.getElementById(link.getAttribute('href').slice(1)) }))
    .filter((chapter) => chapter.heading);

  let railFits = false;
  let frameRequested = false;

  const updatePosition = () => {
    const columnRect = textColumn.getBoundingClientRect();
    const freeSpace = isRtl ? window.innerWidth - columnRect.right : columnRect.left;

    railFits = freeSpace >= RAIL_WIDTH + RAIL_GAP + SCREEN_EDGE;

    if (!railFits) return;

    const left = isRtl ? columnRect.right + RAIL_GAP : columnRect.left - RAIL_GAP - RAIL_WIDTH;

    rail.style.left = `${Math.round(left)}px`;
    rail.style.width = `${RAIL_WIDTH}px`;
  };

  const updateState = () => {
    frameRequested = false;

    const viewportHeight = window.innerHeight;
    const headerOffset = getHeaderOffset();
    const railTop = headerOffset + SPACE_BELOW_HEADER;

    setHeaderOffsetVariable(headerOffset);
    rail.style.setProperty('--blog-rail-top', `${Math.round(railTop)}px`);

    const readingLine = headerOffset + viewportHeight * 0.25;
    const boxBottom = box.getBoundingClientRect().bottom;
    const articleTop = firstContent.getBoundingClientRect().top;
    const articleBottom = lastContent.getBoundingClientRect().bottom;
    const railBottom = railTop + rail.offsetHeight;

    const hasPassedBox = boxBottom < railTop;
    const hasRoomBeforeNextSection = articleBottom - railBottom > SPACE_BEFORE_NEXT_SECTION;

    rail.classList.toggle('is-visible', railFits && hasPassedBox && hasRoomBeforeNextSection);

    let activeIndex = -1;

    chapters.forEach((chapter, index) => {
      if (chapter.heading.getBoundingClientRect().top <= readingLine) {
        activeIndex = index;
      }
    });

    chapters.forEach((chapter, index) => {
      if (index === activeIndex) {
        chapter.link.setAttribute('aria-current', 'location');
      } else {
        chapter.link.removeAttribute('aria-current');
      }
    });

    if (progressBar && track) {
      const articleHeight = Math.max(articleBottom - articleTop, 1);
      const ratio = Math.min(1, Math.max(0, (readingLine - articleTop) / articleHeight));
      progressBar.style.height = `${Math.round(ratio * track.offsetHeight)}px`;
    }
  };

  const requestUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(updateState);
  };

  const handleResize = () => {
    updatePosition();
    requestUpdate();
  };

  updatePosition();
  updateState();

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', handleResize);
  window.addEventListener('load', handleResize);
})();
